import React, { useRef, useEffect, useState } from 'react';
import { RECOGNITIONS, SOCIAL_IMPACT, QUOTES, PROFILE } from '../../data/mock';
import { Globe, MapPin, Rocket, Code2, BookOpen, Award, Quote, Heart, Users } from 'lucide-react';
import { useIsMobile, useIsTablet } from '../../hooks/useWindowSize';

const useInView = (threshold = 0.06) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const iconMap = { Globe, MapPin, Rocket, Code2, BookOpen, Award };

const RecognitionSection = () => {
  const [ref1, inView1] = useInView();
  const [ref2, inView2] = useInView();
  const [ref3, inView3] = useInView();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const pad = isMobile ? '0 20px' : isTablet ? '0 32px' : '0 48px';
  const secPad = isMobile ? '64px 0' : '100px 0';

  const SectionLabel = ({ label }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
      <div style={{ width: '36px', height: '3px', background: '#E62B1E' }} />
      <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 700, letterSpacing: '3px', color: '#E62B1E', textTransform: 'uppercase' }}>{label}</span>
    </div>
  );

  return (
    <>
      {/* Recognition */}
      <section id="recognition" ref={ref1} style={{ background: '#0A0A0A', padding: secPad }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: pad }}>
          <SectionLabel label="Recognition" />
          <h2 style={{
            fontFamily: '"Bebas Neue", sans-serif',
            fontSize: isMobile ? '34px' : 'clamp(36px, 4vw, 56px)',
            color: '#FFFFFF', letterSpacing: '2px', margin: '0 0 40px 0', lineHeight: 1
          }}>FEATURED &amp; RECOGNISED</h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: '16px'
          }}>
            {RECOGNITIONS.map((r, i) => {
              const IconComponent = iconMap[r.icon] || Award;
              return (
                <div key={i} style={{
                  background: '#0E0E0E', border: '1px solid #1A1A1A',
                  borderRadius: '8px', padding: isMobile ? '24px' : '28px',
                  opacity: inView1 ? 1 : 0,
                  transform: inView1 ? 'translateY(0)' : 'translateY(24px)',
                  transition: `opacity 0.6s ease ${i * 0.09}s, transform 0.6s ease ${i * 0.09}s`
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(230,43,30,0.28)'; e.currentTarget.style.background = '#141414'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#1A1A1A'; e.currentTarget.style.background = '#0E0E0E'; }}
                >
                  <div style={{
                    width: '38px', height: '38px', borderRadius: '8px',
                    background: 'rgba(230,43,30,0.08)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    marginBottom: '16px', border: '1px solid rgba(230,43,30,0.12)'
                  }}>
                    <IconComponent size={16} color="#E62B1E" />
                  </div>
                  <h4 style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 600, color: '#FFFFFF', margin: '0 0 8px 0', lineHeight: 1.4 }}>{r.title}</h4>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#5A5A5A', lineHeight: 1.65, margin: 0 }}>{r.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Global Stage */}
      <section style={{ background: '#F5F3EF', padding: secPad }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: pad }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr' : '1fr 460px',
            gap: isMobile ? '36px' : '72px',
            alignItems: 'center'
          }}>
            <div>
              <SectionLabel label="Global Presence" />
              <h2 style={{
                fontFamily: '"Bebas Neue", sans-serif',
                fontSize: isMobile ? '34px' : 'clamp(36px, 4vw, 56px)',
                color: '#0A0A0A', letterSpacing: '2px', margin: '0 0 20px 0', lineHeight: 1
              }}>BUILD WHERE<br />IT <span style={{ color: '#E62B1E' }}>MATTERS</span></h2>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: isMobile ? '14px' : '16px', color: '#5A5A5A', lineHeight: 1.8, margin: '0 0 20px 0' }}>
              During COVID, an entire generation of Indians abroad watched their parents' last rites on a video call. Krupal was already home - not by accident, but by conviction. He'd looked at every reason to leave, and chosen to build here instead.
              </p>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: isMobile ? '14px' : '16px', color: '#6B6B6B', lineHeight: 1.8, margin: '0 0 28px 0' }}>
              That decision didn't limit him. While the world was rethinking where it belonged, he was quietly building software that now runs inside enterprises across India, the UK, the US, Australia, and New Zealand. India wasn't a fallback. It was the bet - and it paid.
              </p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {['India', 'United Kingdom', 'USA', 'Australia', 'New Zealand', 'Dubai'].map((c, i) => (
                  <span key={i} style={{
                    fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 500,
                    color: '#1A1A1A', background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.1)',
                    borderRadius: '4px', padding: '5px 12px'
                  }}>{c}</span>
                ))}
              </div>
            </div>
            <div style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.12)', position: 'relative' }}>
              <img src={PROFILE.photos.dubai} alt="Krupal at GITEX Global, Dubai"
                style={{ width: '100%', height: isMobile ? '300px' : isTablet ? '400px' : '540px', objectFit: 'cover', objectPosition: 'center', display: 'block' }} />
              <div style={{
                position: 'absolute', bottom: '16px', left: '16px',
                background: 'rgba(10,10,10,0.85)', borderRadius: '4px', padding: '7px 14px', backdropFilter: 'blur(8px)'
              }}>
                <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: '#FFFFFF', fontWeight: 500 }}>GITEX Global 2024 · Dubai</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Impact */}
      <section id="impact" ref={ref2} style={{ background: '#0A0A0A', padding: secPad }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: pad }}>
          <SectionLabel label="Social Impact Goals for 2026–27" />
          <h2 style={{
            fontFamily: '"Bebas Neue", sans-serif',
            fontSize: isMobile ? '34px' : 'clamp(36px, 4vw, 56px)',
            color: '#FFFFFF', letterSpacing: '2px', margin: '0 0 40px 0', lineHeight: 1
          }}>BUILDING BEYOND<br /><span style={{ color: '#E62B1E' }}>THE BOTTOM LINE</span></h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '16px'
          }}>
            {SOCIAL_IMPACT.map((s, i) => (
              <div key={i} style={{
                background: '#0E0E0E', border: '1px solid #1A1A1A',
                borderRadius: '8px', padding: isMobile ? '24px' : '32px',
                opacity: inView2 ? 1 : 0,
                transform: inView2 ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 0.6s ease ${i * 0.12}s, transform 0.6s ease ${i * 0.12}s`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <h4 style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', fontWeight: 700, color: '#FFFFFF', margin: '0 0 4px 0' }}>{s.org}</h4>
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: '#E62B1E', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase' }}>{s.role}</span>
                  </div>
                  <Heart size={15} color="rgba(230,43,30,0.35)" style={{ flexShrink: 0 }} />
                </div>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#5A5A5A', lineHeight: 1.7, margin: '0 0 16px 0' }}>{s.description}</p>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(230,43,30,0.07)', borderRadius: '4px', padding: '4px 10px', border: '1px solid rgba(230,43,30,0.12)' }}>
                  <Users size={10} color="#E62B1E" />
                  <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: '#E62B1E', fontWeight: 600 }}>{s.stat}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quotes */}
      <section ref={ref3} style={{ background: '#F5F3EF', padding: secPad }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: pad }}>
          <SectionLabel label="In His Words" />
          <h2 style={{
            fontFamily: '"Bebas Neue", sans-serif',
            fontSize: isMobile ? '34px' : 'clamp(36px, 4vw, 56px)',
            color: '#0A0A0A', letterSpacing: '2px', margin: '0 0 40px 0', lineHeight: 1
          }}>PERSPECTIVES WORTH<br /><span style={{ color: '#E62B1E' }}>SPREADING</span></h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{
              background: '#0A0A0A', borderRadius: '8px',
              padding: isMobile ? '28px 20px' : '48px',
              opacity: inView3 ? 1 : 0,
              transform: inView3 ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease'
            }}>
              <Quote size={isMobile ? 20 : 28} color="#E62B1E" style={{ marginBottom: '14px', opacity: 0.6 }} />
              <p style={{
                fontFamily: '"Bebas Neue", sans-serif',
                fontSize: isMobile ? '20px' : 'clamp(22px, 2.8vw, 36px)',
                color: '#FFFFFF', lineHeight: 1.45, letterSpacing: '1px', margin: '0 0 14px 0'
              }}>"{QUOTES[0].text}"</p>
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: '#E62B1E', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 600 }}>- Krupal Chaudhary{QUOTES[0].context}</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '16px' }}>
              {QUOTES.slice(1).map((q, i) => (
                <div key={i} style={{
                  background: '#FFFFFF', borderRadius: '8px',
                  padding: isMobile ? '22px' : '32px',
                  border: '1px solid rgba(0,0,0,0.07)',
                  opacity: inView3 ? 1 : 0,
                  transform: inView3 ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.6s ease ${(i + 1) * 0.1}s, transform 0.6s ease ${(i + 1) * 0.1}s`
                }}>
                  <Quote size={15} color="#E62B1E" style={{ marginBottom: '10px', opacity: 0.5 }} />
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#1A1A1A', lineHeight: 1.7, margin: '0 0 12px 0', fontStyle: 'italic' }}>"{q.text}"</p>
                  <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: '#9A9A9A', letterSpacing: '1px', textTransform: 'uppercase' }}>- Krupal · {q.context}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default RecognitionSection;
